import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Check, FileText, MessageSquare, Star, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner, EmptyState } from "@/components/shared";
import { toast } from "sonner";

const money = (c, v) => `${c} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StarPick = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="star-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`star-${n}`} aria-label={`${n} stars`}>
        <Star size={26} className={n <= value ? "fill-[#F5B301] text-[#F5B301]" : "text-[#E3E8E8]"} />
      </button>
    ))}
  </div>
);

export default function JobQuotesPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => api.get(`/jobs/${id}/quotes`).then((r) => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { if (data?.job?.my_review) { setRating(data.job.my_review.rating); setReviewBody(data.job.my_review.body); } }, [data]);

  const award = async (qid) => {
    try { await api.post(`/jobs/${id}/award?quote_id=${qid}`); toast.success("Job awarded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pdf = (qid) => { const t = localStorage.getItem("propora_token"); window.open(`${API}/quotes/${qid}/pdf?auth=${t}`, "_blank"); };

  const [rating, setRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");
  const [issueOpen, setIssueOpen] = useState(false);
  const [issue, setIssue] = useState("");

  const confirmDone = async () => {
    try { await api.post(`/jobs/${id}/confirm-completion`); toast.success("Completion confirmed — you can now leave a review"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportIssue = async () => {
    try { await api.post(`/jobs/${id}/report-issue`, { reason: issue }); toast.success("Issue reported — an admin will review"); setIssueOpen(false); setIssue(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitReview = async () => {
    if (!reviewBody.trim()) { toast.error("Please write a short review"); return; }
    try {
      const j = data.job;
      if (j.my_review) await api.put(`/reviews/${j.my_review.id}`, { rating, body: reviewBody });
      else await api.post(`/jobs/${id}/review`, { rating, body: reviewBody });
      toast.success("Thank you — your review is published"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (err) return <div className="propora-container py-16"><EmptyState title="Job not found" /></div>;
  if (!data) return <Spinner />;
  const { job, quotes } = data;
  const awarded = job.awarded_quote_id;

  return (
    <div className="propora-container py-10" data-testid="job-quotes-page">
      <Link to="/dashboard/jobs" className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83] mb-6"><ArrowLeft size={16} /> Back to my jobs</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">{job.title}</h1>
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${job.status === "awarded" ? "bg-blue-50 text-blue-700" : "bg-[#E8F5F1] text-[#007F83]"}`}>{job.status}</span>
      </div>
      <p className="text-[#4A5568] mt-2">{job.service_name} · {job.area} · {job.response_count}/{job.max_responses} responses</p>

      {awarded && (
        <div className="mt-6 bg-white rounded-2xl border border-[#E3E8E8] p-6" data-testid="completion-card">
          <h2 className="font-display font-bold text-xl text-[#0B1733] mb-1">Job progress</h2>
          <p className="text-sm text-[#4A5568] mb-4">Awarded to <b className="text-[#0B1733]">{job.awarded_company_name}</b></p>

          {job.status === "awarded" && (
            <p className="text-sm text-[#4A5568] inline-flex items-center gap-2" data-testid="awaiting-finish"><CheckCircle2 size={16} className="text-[#007F83]" /> Awaiting the company to mark the work finished. Chat with them in <Link to="/dashboard/messages" className="text-[#007F83] underline">Messages</Link>.</p>
          )}

          {job.status === "completion_pending" && (
            <div className="space-y-3" data-testid="confirm-section">
              <p className="text-sm text-[#0B1733]">The company has marked this work as finished. Please confirm it's complete, or report an issue.</p>
              <div className="flex gap-2">
                <Button onClick={confirmDone} data-testid="confirm-completion-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Check size={16} className="mr-1" /> Confirm completion</Button>
                <Button variant="outline" onClick={() => setIssueOpen(true)} data-testid="report-issue-btn" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><AlertTriangle size={16} className="mr-1" /> Report an issue</Button>
              </div>
            </div>
          )}

          {job.status === "disputed" && (
            <p className="text-sm text-amber-700 inline-flex items-center gap-2" data-testid="disputed-note"><AlertTriangle size={16} /> You reported an issue. An admin is reviewing it.</p>
          )}

          {job.status === "completed" && (
            <div data-testid="review-section">
              <p className="text-sm text-green-700 inline-flex items-center gap-2 mb-4"><CheckCircle2 size={16} /> Completed{job.completed_at ? "" : ""}.</p>
              {(job.can_review || job.my_review) ? (
                <div className="border-t border-[#E3E8E8] pt-4">
                  <h3 className="font-display font-bold text-[#0B1733] mb-2">{job.my_review ? "Your review" : "Leave a review"}</h3>
                  <StarPick value={rating} onChange={setRating} />
                  <Textarea value={reviewBody} onChange={(e) => setReviewBody(e.target.value)} rows={3} placeholder="Share your experience with this company" data-testid="review-body" className="mt-3" />
                  <Button onClick={submitReview} data-testid="submit-review-btn" className="mt-3 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">{job.my_review ? "Update review" : "Publish review"}</Button>
                  {job.my_review?.edited && <span className="ml-3 text-xs text-[#4A5568]">(edited)</span>}
                </div>
              ) : (
                <p className="text-sm text-[#4A5568]">Reviews can be left only by the customer on this completed job.</p>
              )}
            </div>
          )}
        </div>
      )}

      <h2 className="font-display font-bold text-2xl text-[#0B1733] mt-8 mb-4">Quotes received ({quotes.length})</h2>
      {quotes.length === 0 ? (
        <EmptyState title="No quotes yet" subtitle="Matching companies have been notified. Quotes will appear here as they respond." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {quotes.map((q) => {
            const isWinner = awarded === q.id;
            return (
              <div key={q.id} className={`bg-white rounded-2xl border p-6 ${isWinner ? "border-[#007F83] ring-1 ring-[#007F83]" : "border-[#E3E8E8]"}`} data-testid={`quote-${q.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-lg text-[#0B1733]">{q.company_name}</h3>
                  <span className="text-xs text-[#4A5568]">v{q.version} · valid to {q.valid_until}</span>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  {q.line_items.map((li, i) => (
                    <div key={i} className="flex justify-between text-[#4A5568]">
                      <span>{li.description} <span className="text-xs">×{li.quantity} {li.unit}</span></span>
                      <span>{money(q.currency, li.quantity * li.unit_price)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-[#E3E8E8] space-y-1 text-sm">
                  <div className="flex justify-between text-[#4A5568]"><span>Subtotal</span><span>{money(q.currency, q.subtotal)}</span></div>
                  {q.discount > 0 && <div className="flex justify-between text-[#4A5568]"><span>Discount</span><span>- {money(q.currency, q.discount)}</span></div>}
                  <div className="flex justify-between text-[#4A5568]"><span>{q.vat_mode === "not_applicable" ? "VAT (n/a)" : `VAT ${q.vat_rate}%${q.vat_mode === "inclusive" ? " incl." : ""}`}</span><span>{money(q.currency, q.vat_amount)}</span></div>
                  <div className="flex justify-between font-display font-extrabold text-[#0B1733] text-base pt-1"><span>Total</span><span>{money(q.currency, q.total)}</span></div>
                </div>
                {q.payment_terms && <p className="mt-3 text-xs text-[#4A5568]"><b>Payment:</b> {q.payment_terms}</p>}
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => pdf(q.id)} data-testid={`quote-pdf-${q.id}`} className="rounded-full"><Download size={15} className="mr-1" /> PDF</Button>
                  {isWinner ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#007F83] px-3"><Check size={16} /> Awarded</span>
                  ) : !awarded ? (
                    <Button size="sm" onClick={() => award(q.id)} data-testid={`award-${q.id}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Award this quote</Button>
                  ) : (
                    <span className="text-sm text-[#4A5568] px-2">Not selected</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-6 p-4 rounded-2xl bg-[#E8F5F1] text-sm text-[#0B1733] flex items-start gap-2">
        <MessageSquare size={18} className="shrink-0 mt-0.5 text-[#007F83]" />
        <span>Message companies privately from your <Link to="/dashboard/messages" className="font-semibold underline">Messages</Link>. Awarding a job does not transfer money or create escrow — payment terms are agreed directly with the company.</span>
      </div>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent data-testid="issue-dialog">
          <DialogHeader><DialogTitle className="font-display">Report an issue</DialogTitle>
          <DialogDescription>Tell us what went wrong. An admin will review and resolve with an audit trail.</DialogDescription></DialogHeader>
          <Textarea value={issue} onChange={(e) => setIssue(e.target.value)} rows={4} placeholder="Describe the issue" data-testid="issue-reason" />
          <Button onClick={reportIssue} data-testid="issue-submit-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Submit report</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
